import React from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { ProductLine } from '@kouto/types';
import { Divider } from 'components/common/styled/common-styled';
import { useLandingPageRouteParams } from 'hooks/useLandingPageRouteParams';
import { getProductPageTitle } from 'utils/listings';
import LandingPageFilters from './LandingPageFilters';

interface LandingPageHeaderProps {
  /** Schedule-variant layout: hides the title and switches to a page-centered filter row
      (1fr auto 1fr grid) instead of the standard title-left, filters-right flex layout. */
  isScheduleView?: boolean;
  product?: ProductLine;
}

const LandingPageHeader = ({
  isScheduleView = false,
  product,
}: LandingPageHeaderProps) => {
  const { t: translate } = useTranslation();
  const { isResults } = useLandingPageRouteParams();
  const prefix = isScheduleView ? 'schedule-view__' : '';
  const title = getProductPageTitle({
    product,
    translate,
  });
  const showTitle = !isResults && !isScheduleView;

  return (
    <>
      <Header
        className={`${prefix}landing-page-header`}
        isScheduleView={isScheduleView}
      >
        {showTitle && (
          <Title className={`${prefix}landing-page-header-title`}>
            {title}
          </Title>
        )}
        <LandingPageFilters />
      </Header>
      {isScheduleView && (
        <Divider
          className="schedule-view__header-separator"
          space={24}
          style={{ marginBottom: '48px' }}
        />
      )}
    </>
  );
};

export default LandingPageHeader;

/* Default (flex): title (when present) claims `margin-right: auto`, which shoves the Date +
   filter buttons group to the right of the row. When the title is absent (results view) the
   auto margin disappears with it, so Date + buttons sit adjacent at the left instead.
   Schedule view (grid, 1fr auto 1fr): title is hidden; date occupies the left rail (col 1)
   and buttons occupy col 2, page-centered by symmetric 1fr rails on either side. */
const Header = styled.div<{ isScheduleView: boolean }>`
  width: 100%;
  min-height: 40px;
  margin-top: 24px;
  margin-bottom: ${({ isScheduleView }) => (isScheduleView ? '0' : '44px')};
  gap: ${({ isScheduleView }) => (isScheduleView ? '0' : '24px')};
  align-items: center;

  ${({ isScheduleView }) =>
    isScheduleView
      ? css`
          display: grid;
          grid-template-columns: 1fr auto 1fr;
        `
      : css`
          display: flex;
          justify-content: flex-start;
        `}

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
`;

const Title = styled.h2`
  margin: 0;
  /* Pushes subsequent flex siblings (Date + filter buttons) to the right edge of the row. */
  margin-right: auto;
  font-size: 32px;
  line-height: 38px;
  color: var(--way-colors-primaryTextColor);

  @media (max-width: 768px) {
    font-size: 24px;
    line-height: 30px;
  }
`;
